/**
 * BlockFormatForm Component
 *
 * Dialog form for creating/editing block formats.
 * Part of US-P2-003: Substrate Production & Block Creation
 */

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useBlockFormatMutations } from '@/hooks/useSubstrateResources'
import { AlertTriangle, Boxes } from 'lucide-react'

export default function BlockFormatForm({
  isOpen,
  onClose,
  onSave,
  editingFormat = null
}) {
  const [formData, setFormData] = useState({
    name: '',
    reference_code: '',
    supplier: '',
    weight_kg: '',
    cost_per_unit: '',
    current_inventory: 0,
    min_stock_level: ''
  })
  const [error, setError] = useState(null)

  const { createBlockFormat, updateBlockFormat, loading: saving } = useBlockFormatMutations()

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      if (editingFormat) {
        setFormData({
          name: editingFormat.name || '',
          reference_code: editingFormat.reference_code || '',
          supplier: editingFormat.supplier || '',
          weight_kg: editingFormat.weight_kg || '',
          cost_per_unit: editingFormat.cost_per_unit || '',
          current_inventory: editingFormat.current_inventory || 0,
          min_stock_level: editingFormat.min_stock_level || ''
        })
      } else {
        setFormData({
          name: '',
          reference_code: '',
          supplier: '',
          weight_kg: '',
          cost_per_unit: '',
          current_inventory: 0,
          min_stock_level: ''
        })
      }
      setError(null)
    }
  }, [isOpen, editingFormat])

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    setError(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    // Validation
    if (!formData.name.trim()) {
      setError('Format name is required')
      return
    }

    if (!formData.weight_kg || parseFloat(formData.weight_kg) <= 0) {
      setError('Weight per block must be greater than 0')
      return
    }

    try {
      const formatData = {
        name: formData.name.trim(),
        reference_code: formData.reference_code.trim() || null,
        supplier: formData.supplier.trim() || null,
        weight_kg: parseFloat(formData.weight_kg),
        cost_per_unit: formData.cost_per_unit ? parseFloat(formData.cost_per_unit) : null,
        current_inventory: parseInt(formData.current_inventory) || 0,
        min_stock_level: formData.min_stock_level ? parseInt(formData.min_stock_level) : null
      }

      let savedFormat
      if (editingFormat) {
        savedFormat = await updateBlockFormat({ id: editingFormat.id, ...formatData })
      } else {
        savedFormat = await createBlockFormat(formatData)
      }

      onSave?.(savedFormat)
    } catch (err) {
      setError(err.message || 'Failed to save block format')
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Boxes className="h-5 w-5" />
            {editingFormat ? 'Edit Block Format' : 'Add Block Format'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Format Name and Ref Code */}
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2 space-y-2">
              <Label htmlFor="name">Format Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., XLS-B, Satrise 20x15x50"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reference_code">Item Ref</Label>
              <Input
                id="reference_code"
                value={formData.reference_code}
                onChange={(e) => handleChange('reference_code', e.target.value.toUpperCase().slice(0, 6))}
                placeholder="AUTO"
                maxLength={6}
                className="uppercase"
              />
            </div>
          </div>

          {/* Supplier */}
          <div className="space-y-2">
            <Label htmlFor="supplier">Supplier</Label>
            <Input
              id="supplier"
              value={formData.supplier}
              onChange={(e) => handleChange('supplier', e.target.value)}
              placeholder="e.g., Unicorn Bags"
            />
          </div>

          {/* Weight and Cost */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="weight_kg">Weight per Block (kg) *</Label>
              <Input
                id="weight_kg"
                type="number"
                step="0.1"
                min="0.1"
                value={formData.weight_kg}
                onChange={(e) => handleChange('weight_kg', e.target.value)}
                placeholder="4.5"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cost_per_unit">Cost per Unit (GBP)</Label>
              <Input
                id="cost_per_unit"
                type="number"
                step="0.01"
                min="0"
                value={formData.cost_per_unit}
                onChange={(e) => handleChange('cost_per_unit', e.target.value)}
                placeholder="0.45"
              />
            </div>
          </div>

          {/* Inventory */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="current_inventory">Current Stock</Label>
              <Input
                id="current_inventory"
                type="number"
                min="0"
                value={formData.current_inventory}
                onChange={(e) => handleChange('current_inventory', e.target.value)}
                placeholder="0"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="min_stock_level">Min Stock Level</Label>
              <Input
                id="min_stock_level"
                type="number"
                min="0"
                value={formData.min_stock_level}
                onChange={(e) => handleChange('min_stock_level', e.target.value)}
                placeholder="100"
              />
              <p className="text-xs text-content-tertiary">
                Alert threshold for low stock warnings
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving...' : (editingFormat ? 'Update Format' : 'Add Format')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
